import * as React from "react";
import * as ReactDOM from "react-dom";
import { observer } from "mobx-react";
import styled from "styled-components";
import { RotateLoader, RelativeTime } from "@lib/components";
import {
	FaFilter,
	FaSync,
	FaDownload,
	FaCog,
	FaSearch,
	FaCalendarCheck,
	FaCalendarTimes,
	FaCalendarAlt,
	FaUser,
	FaBookOpen,
} from "react-icons/fa";
import { inject } from "mobx-react";
import { RootStore } from "../../../../../mobx/store";
import { withTranslation, WithTranslation } from "react-i18next";
import { BookingsTableStore, BookingsTableFiltersState } from "./bookings-protable-store";
import { BookingsProtableFilters } from "./bookings-protable-filters";
import { BookingsProtableExport } from "./bookings-protable-export";
import { BookingsCalendar } from "./calendar";
import type { CalendarSlotPrefill } from "./booking-create-modal";
import { BookingNotesIndicator } from "./booking-notes-indicator";

// ─── Styled Components ───────────────────────────────────────────────────────

const CardWrap = styled.div`
	background: #fff;
	border: 1px solid #e2e8f0;
	border-radius: 12px;
	overflow: hidden;
	box-sizing: border-box;
`;

const Toolbar = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
	padding: 8px 16px;
	flex-wrap: wrap;
	min-height: 56px;
`;

const PillTabGroup = styled.div`
	display: inline-flex;
	background: #e2e8f0;
	border: 1px solid #e9eaeb;
	border-radius: 10px;
	padding: 4px;
	flex-shrink: 0;
`;

const PillTabBtn = styled.button<{ active?: boolean }>`
	height: 36px;
	padding: 0 12px;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 600;
	border: none;
	cursor: pointer;
	white-space: nowrap;
	background: ${({ active }) => (active ? "#fff" : "transparent")};
	color: ${({ active }) => (active ? "#fb2f2f" : "#414651")};
	box-shadow: ${({ active }) =>
		active
			? "0 1px 3px rgba(10,13,18,0.1), 0 1px 2px -1px rgba(10,13,18,0.1)"
			: "none"};
	transition: background 0.15s, color 0.15s, box-shadow 0.15s;
`;

const SearchBox = styled.label`
	display: flex;
	align-items: center;
	gap: 8px;
	background: #fff;
	border: 1px solid #e2e8f0;
	border-radius: 8px;
	padding: 6px 12px;
	flex: 1;
	max-width: 300px;
	min-width: 160px;
	color: #94a3b8;
	cursor: text;
	height: stretch;
`;

const SearchInput = styled.input`
	border: none;
	outline: none;
	flex: 1;
	font-size: 14px;
	color: #1e293b;
	background: transparent;
	min-width: 0;
	&::placeholder {
		color: #64748b;
	}
`;

const ActionBtns = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin-left: auto;
	flex-shrink: 0;
`;

const IconBtn = styled.button<{ active?: boolean }>`
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	border: 1px solid #d5d7da;
	border-radius: 4px;
	background: ${({ active }) => (active ? "#f1f5f9" : "#fff")};
	cursor: pointer;
	color: #414651;
	flex-shrink: 0;
	transition: background 0.1s;
	&:hover {
		background: #f8f9fa;
	}
`;

const PanelWrap = styled.div`
	border-top: 1px solid #e2e8f0;
`;

const RowsWrap = styled.div`
	padding: 8px 0;
	display: flex;
	flex-direction: column;
	gap: 0;
`;

const BookingRowWrap = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px 16px;
	background: #f8f9fa;
	border: 1px solid #e2e8f0;
	border-radius: 8px;
	margin: 4px 16px;
	cursor: pointer;
	transition: background 0.1s;
	&:hover {
		background: #f1f5f9;
	}
`;

const StatusCircle = styled.div<{ bg: string }>`
	width: 32px;
	height: 32px;
	min-width: 32px;
	border-radius: 50%;
	background: ${({ bg }) => bg};
	display: flex;
	align-items: center;
	justify-content: center;
	color: #fff;
	font-size: 14px;
`;

const RowName = styled.span`
	font-size: 12px;
	font-weight: 600;
	color: #1e293b;
	white-space: nowrap;
	flex-shrink: 0;
	min-width: 140px;
`;

const RowMeta = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	flex: 1;
	min-width: 0;
	font-size: 12px;
	font-weight: 500;
	color: #414651;
	overflow: hidden;
`;

const MetaIcon = styled.span`
	display: inline-flex;
	align-items: center;
	flex-shrink: 0;
	color: #94a3b8;
	font-size: 14px;
`;

const MetaText = styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const StatusBadge = styled.span<{ status: string }>`
	display: inline-flex;
	align-items: center;
	height: 18px;
	padding: 2px 8px 2px 6px;
	border-radius: 9999px;
	font-size: 12px;
	font-weight: 500;
	white-space: nowrap;
	flex-shrink: 0;
	${({ status }) => {
		switch (status) {
			case "confirmed":
				return "background: #ecfdf3; border: 1px solid #abefc6; color: #067647;";
			case "seated":
			case "complete":
				return "background: #ecfdf3; border: 1px solid #abefc6; color: #067647;";
			case "cancelled":
				return "background: #fef3f2; border: 1px solid #fecdca; color: #b42318;";
			default:
				return "background: #fffaeb; border: 1px solid #fedf89; color: #b54708;";
		}
	}}
`;

const LoaderWrap = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 40px 16px;
`;

const EmptyMsg = styled.p`
	text-align: center;
	color: #64748b;
	padding: 40px 16px;
	font-size: 14px;
	margin: 0;
`;

const PaginationBar = styled.div`
	display: flex;
	align-items: center;
	padding: 10px 24px;
	border-top: 1px solid #e2e8f0;
`;

const PageInfo = styled.span`
	font-size: 14px;
	font-weight: 500;
	color: #414651;
`;

const PageBtns = styled.div`
	display: flex;
	gap: 12px;
	margin-left: auto;
`;

const PageBtn = styled.button`
	height: 32px;
	padding: 0 12px;
	border: 1px solid #d5d7da;
	border-radius: 8px;
	background: #fff;
	font-size: 14px;
	font-weight: 600;
	color: #414651;
	cursor: pointer;
	transition: background 0.1s;
	&:hover:not(:disabled) {
		background: #f8f9fa;
	}
	&:disabled {
		opacity: 0.45;
		cursor: not-allowed;
	}
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const COMPLETE_STATUS_COLOR = "#51A351";

function getStatusBg(status: string) {
	switch (status) {
		case "confirmed":
			return "#009100";
		case "seated":
		case "complete":
			return COMPLETE_STATUS_COLOR;
		case "cancelled":
			return "#bc362f";
		default:
			return "#e5ac00";
	}
}

function BookingStatusIcon({ status }: { status: string }) {
	if (status === "confirmed" || status === "seated" || status === "complete") {
		return <FaCalendarCheck />;
	}
	if (status === "cancelled") return <FaCalendarTimes />;
	return <FaCalendarAlt />;
}

// ─── Props / State ────────────────────────────────────────────────────────────

interface Props extends WithTranslation {
	store?: RootStore;
	restaurantId: string;
	items: T.Schema.Booking.BookingSchema[];
	count: number;
	exportUrl: string;
	screenWidth: number;
	region: T.Schema.Restaurant.RestaurantSchema["settings"]["region"];
	query: T.Lib.Table.Query;
	fetch: T.Lib.Table.FetchFunction;
	onClick: (item: T.Schema.Booking.BookingSchema) => void;
	persistedFilters?: BookingsTableFiltersState;
	onPersistFilters?: (state: BookingsTableFiltersState) => void;
	/** Show the List/Calendar tab toggle */
	calendarAllowed: boolean;
	defaultDashboardView?: "list" | "calendar";
	onDashboardViewChange?: (view: "list" | "calendar") => void;
	/** Forwarded to BookingsCalendar */
	calendarReloadSignal: number;
	liveBookings: T.Schema.Booking.BookingSchema[];
	liveBookingsKey: string;
	suppressCalendarSelection: boolean;
	onRequestCreateAtSlot: (slot: CalendarSlotPrefill) => void;
}

interface BLVState {
	view: "list" | "calendar";
	searchInput: string;
}

// ─── Component ───────────────────────────────────────────────────────────────

@observer
class BookingsListViewClass extends React.Component<Props, BLVState> {
	store: BookingsTableStore<T.Schema.Booking.BookingSchema>;
	/** Container element for the calendar toolbar portal (inline with tabs). */
	private calendarToolbarEl: HTMLElement | null = null;
	private setCalendarToolbarRef = (el: HTMLDivElement | null) => {
		if (el && this.calendarToolbarEl !== el) {
			this.calendarToolbarEl = el;
			this.forceUpdate(); // re-render so portal can mount
		}
	};

	constructor(props: Props) {
		super(props);
		this.state = {
			view: props.defaultDashboardView ?? "list",
			searchInput: props.persistedFilters?.search || "",
		};
		this.store = new BookingsTableStore<T.Schema.Booking.BookingSchema>(
			props.fetch,
			props.region.timezone,
			{
				initial: props.persistedFilters,
				onPersist: props.onPersistFilters,
			}
		);
		this.store.update(
			{ items: props.items, count: props.count },
			{ skipPersist: true }
		);
	}

	componentDidMount() {
		this.store.fetch();
	}

	componentDidUpdate(prevProps: Props) {
		if (
			prevProps.items !== this.props.items ||
			prevProps.count !== this.props.count
		) {
			this.store.update(
				{ items: this.props.items, count: this.props.count },
				{ skipPersist: true }
			);
		}
		if (
			prevProps.defaultDashboardView !== this.props.defaultDashboardView &&
			this.props.defaultDashboardView != null &&
			this.state.view !== this.props.defaultDashboardView
		) {
			this.setState({ view: this.props.defaultDashboardView });
		}
	}

	setDashboardView = (view: "list" | "calendar") => {
		this.setState({ view });
		this.props.onDashboardViewChange?.(view);
	};

	toggleDropdown = (d: "" | "filters" | "export") => {
		this.store.update({
			dropdown: this.store.s.dropdown === d ? "" : d,
		});
	};

	handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			this.store.update({ search: this.state.searchInput, page: 1 });
			this.store.fetch();
		}
	};

	render() {
		const {
			t,
			calendarAllowed,
			calendarReloadSignal,
			liveBookings,
			liveBookingsKey,
			suppressCalendarSelection,
			onRequestCreateAtSlot,
			exportUrl,
			region,
			query,
		} = this.props;
		const { view, searchInput } = this.state;
		const { items, loading, error, dropdown, page } = this.store.s;
		const totalPages = this.store.totalPages;

		return (
			<CardWrap>
				{/* ── Toolbar ── */}
				<Toolbar>
					{calendarAllowed && (
						<PillTabGroup>
							<PillTabBtn
								active={view === "list"}
								onClick={() => this.setDashboardView("list")}
							>
								List View
							</PillTabBtn>
							<PillTabBtn
								active={view === "calendar"}
								onClick={() => this.setDashboardView("calendar")}
							>
								Calendar View
							</PillTabBtn>
						</PillTabGroup>
					)}

					{/* Calendar toolbar controls portal slot — filled by BookingsCalendar */}
					{view === "calendar" && (
						<div
							ref={this.setCalendarToolbarRef}
							style={{
								display: "flex",
								alignItems: "center",
								gap: 8,
								flex: 1,
								minWidth: 0,
								overflowX: "auto",
								justifyContent: "flex-end",
							}}
						/>
					)}

					{view === "list" && (
						<div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
							<SearchBox>
								<MetaIcon>
									<FaSearch style={{ fontSize: 14 }} />
								</MetaIcon>
								<SearchInput
									placeholder="Search customers..."
									value={searchInput}
									onChange={(e) =>
										this.setState({ searchInput: e.target.value })
									}
									onKeyDown={this.handleSearchKeyDown}
								/>
							</SearchBox>

							<ActionBtns>
								<IconBtn
									active={dropdown === "filters"}
									title="Filter & Sort"
									onClick={() => this.toggleDropdown("filters")}
								>
									<FaFilter style={{ fontSize: 13 }} />
								</IconBtn>
								<IconBtn
									title="Refresh"
									onClick={() => this.store.fetch()}
								>
									<FaSync style={{ fontSize: 13 }} />
								</IconBtn>
								<IconBtn
									active={dropdown === "export"}
									title="Export"
									onClick={() => this.toggleDropdown("export")}
								>
									<FaDownload style={{ fontSize: 13 }} />
								</IconBtn>
								<IconBtn
									title="Advanced Filters"
									active={dropdown === "filters"}
									onClick={() => this.toggleDropdown("filters")}
								>
									<FaCog style={{ fontSize: 13 }} />
								</IconBtn>
							</ActionBtns>
						</div>
					)}
				</Toolbar>

				{/* ── Filter / Export Panels ── */}
				{view === "list" && (dropdown === "filters" || dropdown === "export") && (
					<PanelWrap>
						<BookingsProtableFilters
							store={this.store}
							query={query}
							region={region}
						/>
						<BookingsProtableExport
							store={this.store}
							exportUrl={exportUrl}
							region={region}
						/>
					</PanelWrap>
				)}

				{/* ── Calendar View ── */}
				{view === "calendar" && (
					<BookingsCalendar
						reloadSignal={calendarReloadSignal}
						liveBookings={liveBookings}
						liveBookingsKey={liveBookingsKey}
						suppressSelection={suppressCalendarSelection}
						onRequestCreateAtSlot={onRequestCreateAtSlot}
						{...({ toolbarContainer: this.calendarToolbarEl } as any)}
					/>
				)}

				{/* ── List View ── */}
				{view === "list" && (
					<>
						<RowsWrap>
							{loading && (
								<LoaderWrap>
									<RotateLoader size={4} className="center" />
								</LoaderWrap>
							)}
							{!loading && error && (
								<EmptyMsg className="error-text">{error}</EmptyMsg>
							)}
							{!loading && !error && items.length === 0 && (
								<EmptyMsg>No bookings found</EmptyMsg>
							)}
							{!loading &&
								!error &&
								items
									.filter((item) => (item.status as string) !== "draft")
									.map((item, i) => (
										<BookingRowWrap
											key={item._id || i}
											onClick={() => this.props.onClick(item)}
										>
											{/* Status icon */}
											<StatusCircle bg={getStatusBg(item.status)}>
												<BookingStatusIcon status={item.status} />
											</StatusCircle>

											{/* Name */}
											<RowName>
												<span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }}>
													<span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
														{item.number} - {item.customer.name}
													</span>
													<BookingNotesIndicator notes={item.notes} compact />
												</span>
											</RowName>

											{/* Booking date */}
											<RowMeta>
												<MetaIcon>
													<FaCalendarAlt />
												</MetaIcon>
												<MetaText>
													{t("datetimeFromTimestamp", {
														value: item.config.timestamp,
													})}
												</MetaText>
											</RowMeta>

											{/* Party size */}
											<RowMeta>
												<MetaIcon>
													<FaUser />
												</MetaIcon>
												<MetaText>
													{item.config.number_of_people} pax
												</MetaText>
											</RowMeta>

											{/* Placed date */}
											<RowMeta>
												<MetaIcon>
													<FaCalendarAlt />
												</MetaIcon>
												<MetaText>
													{t("datetimeFromTimestamp", {
														value: item.created,
													})}
												</MetaText>
											</RowMeta>

											{/* Time ago */}
											<RowMeta>
												<MetaIcon>
													<FaBookOpen />
												</MetaIcon>
												<MetaText>
													<RelativeTime timestamp={item.created} />
												</MetaText>
											</RowMeta>

											{/* Status badge */}
											<StatusBadge status={item.status}>
												{t(`order.status.${item.status}`)}
											</StatusBadge>
										</BookingRowWrap>
									))}
						</RowsWrap>

						{/* ── Pagination ── */}
						<PaginationBar>
							<PageInfo>
								Page {page} of {totalPages}
							</PageInfo>
							<PageBtns>
								<PageBtn
									disabled={page <= 1}
									onClick={() => this.store.setPage(page - 1)}
								>
									Previous
								</PageBtn>
								<PageBtn
									disabled={page >= totalPages}
									onClick={() => this.store.setPage(page + 1)}
								>
									Next
								</PageBtn>
							</PageBtns>
						</PaginationBar>
					</>
				)}
			</CardWrap>
		);
	}
}

export const BookingsListView = inject("store")(withTranslation()(BookingsListViewClass));
